import { useState, useEffect, useCallback, useRef } from "react";
import { aGet, aPost, aPut, aDelete } from "../../utils/adminApi";
import {
  C,
  Input,
  Select,
  Btn,
  Table,
  Pagination,
  Modal,
  Badge,
} from "./AdminPanel";
import ImageUpload from "./ImageUpload";
import "../../styles/products.css";

// React Icons imports
import {
  FiSearch,
  FiTrash2,
  FiPlus,
  FiEdit2,
  FiX,
  FiPackage,
  FiImage,
  FiTag,
  FiStar,
  FiFolder,
} from "react-icons/fi";
import { FaRupeeSign, FaRuler } from "react-icons/fa";
import { MdInventory, MdDescription } from "react-icons/md";

const DEFAULT_SIZES = [
  { name: "Regular (2.0m)", price: null, stock: 0 },
  { name: "Large (2.25m)", price: null, stock: 0 },
  { name: "XL (2.50m)", price: null, stock: 0 },
];

const emptyProduct = {
  name: "",
  brand: "KKB",
  category: "",
  subcategory: "",
  price: "",
  originalPrice: "",
  description: "",
  image: "",
  images: [],
  badge: "",
  inStock: true,
  stock: 100,
  featured: false,
  sizes: JSON.parse(JSON.stringify(DEFAULT_SIZES)),
  features: [],
};

// Fallback categories
const FALLBACK_CATEGORIES = [
  { _id: "1", name: "Lungies", isActive: true },
  { _id: "2", name: "Kaavi", isActive: true },
  { _id: "3", name: "Bedsheets", isActive: true },
  { _id: "4", name: "Towel", isActive: true },
];

export default function Products({ toast }) {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState([]);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const debounceRef = useRef();
  const abortControllerRef = useRef(null);

  const normalizeImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("https://lightslategrey-mole-360881.hostingersite.com")) return url;
    if (url.startsWith("/uploads")) return `https://lightslategrey-mole-360881.hostingersite.com${url}`;
    if (!url.includes("/") && !url.includes("http")) {
      return `https://lightslategrey-mole-360881.hostingersite.com/uploads/products/${url}`;
    }
    return url;
  };

  const loadCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const response = await aGet("/categories");
      if (response && response.categories && response.categories.length > 0) {
        setCategories(response.categories.filter((cat) => cat.isActive));
      }
    } catch (error) {
      console.error("Error loading categories:", error);
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  const load = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const response = await aGet("/products", {
        page,
        search,
        category,
        signal: abortControllerRef.current.signal,
      });

      // Map MySQL 'id' to '_id' for frontend compatibility
      const productsWithId = (response.products || []).map((product) => ({
        ...product,
        _id: product.id, // Add _id field for compatibility
        id: product.id,
      }));

      setProducts(productsWithId);
      setTotal(response.total || 0);
      setPages(response.pages || 1);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error loading products:", error);
        toast(error.message || "Failed to load products", "error");
      }
    } finally {
      setLoading(false);
    }
  }, [page, search, category, toast]);

  useEffect(() => {
    loadCategories();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      load();
    }, 400);

    return () => clearTimeout(debounceRef.current);
  }, [load]);

  // Generate subcategory from category name (slug format)
  const generateSubcategoryFromCategory = (categoryName) => {
    if (!categoryName) return "";
    return categoryName.toLowerCase().replace(/\s+/g, "-") + "-collection";
  };

  const openAdd = () => {
    const defaultCategory = categories[0]?.name || "";
    setForm({
      ...emptyProduct,
      category: defaultCategory,
      subcategory: generateSubcategoryFromCategory(defaultCategory),
      sizes: JSON.parse(JSON.stringify(DEFAULT_SIZES)),
      image: "",
      images: [],
    });
    setModal("add");
  };

  const openEdit = (p) => {
    let sizes = [];
    if (p.sizes && Array.isArray(p.sizes) && p.sizes.length > 0) {
      sizes = p.sizes;
    } else if (typeof p.sizes === "string") {
      try {
        sizes = JSON.parse(p.sizes);
      } catch (e) {
        sizes = JSON.parse(JSON.stringify(DEFAULT_SIZES));
      }
    } else {
      sizes = JSON.parse(JSON.stringify(DEFAULT_SIZES));
    }

    // Parse images if it's a string
    let images = p.images || [];
    if (typeof images === "string") {
      try {
        images = JSON.parse(images);
      } catch (e) {
        images = [];
      }
    }
    // Ensure images is always an array
    if (!Array.isArray(images)) {
      images = [];
    }

    setForm({
      ...p,
      _id: p.id, // ← IMPORTANT: Map MySQL 'id' to '_id' for frontend compatibility
      id: p.id, // Keep both for safety
      price: p.price || "",
      originalPrice: p.originalPrice || "",
      sizes: sizes,
      images: images,
      subcategory: p.subcategory || generateSubcategoryFromCategory(p.category),
    });
    setModal("edit");
  };

  const handleSizeChange = (index, field, value) => {
    const newSizes = [...form.sizes];
    newSizes[index] = { ...newSizes[index], [field]: value };
    setForm({ ...form, sizes: newSizes });
  };

  const addSize = () => {
    setForm({
      ...form,
      sizes: [...form.sizes, { name: "", price: null, stock: 0 }],
    });
  };

  const removeSize = (index) => {
    if (form.sizes.length === 1) {
      toast("At least one size is required", "error");
      return;
    }
    const newSizes = form.sizes.filter((_, i) => i !== index);
    setForm({ ...form, sizes: newSizes });
  };

  // Auto-generate subcategory when category changes
  const handleCategoryChange = (categoryName) => {
    const autoSubcategory = generateSubcategoryFromCategory(categoryName);
    setForm({
      ...form,
      category: categoryName,
      subcategory: autoSubcategory,
    });
  };

  const handleSave = async () => {
    if (!form.name) {
      toast("Product name is required", "error");
      return;
    }
    if (!form.price) {
      toast("Price is required", "error");
      return;
    }
    if (!form.category) {
      toast("Please select a category", "error");
      return;
    }
    if (!form.image) {
      toast("Please upload a product image", "error");
      return;
    }

    for (let i = 0; i < form.sizes.length; i++) {
      if (!form.sizes[i].name) {
        toast(`Size ${i + 1} name is required`, "error");
        return;
      }
    }

    setSaving(true);
    try {
      const totalStock = form.sizes.reduce(
        (sum, size) => sum + (Number(size.stock) || 0),
        0,
      );

      const productData = {
        name: form.name,
        brand: form.brand || "KKB",
        price: Number(form.price),
        originalPrice: form.originalPrice
          ? Number(form.originalPrice)
          : undefined,
        category: form.category,
        subcategory:
          form.subcategory || generateSubcategoryFromCategory(form.category),
        description: form.description || "",
        image: form.image,
        images: JSON.stringify(Array.isArray(form.images) ? form.images : []),
        badge: form.badge || null,
        inStock: totalStock > 0,
        stock: totalStock,
        featured: form.featured || false,
        sizes: JSON.stringify(
          form.sizes.map((s) => ({
            name: s.name,
            price: s.price ? Number(s.price) : null,
            stock: Number(s.stock) || 0,
          })),
        ),
      };

      if (modal === "add") {
        await aPost("/products", productData);
        toast("Product created successfully ✓");
      } else {
        // Use 'id' or '_id' - whichever exists
        const productId = form.id || form._id;
        if (!productId) {
          throw new Error("Product ID is missing");
        }
        await aPut(`/products/${productId}`, productData);
        toast("Product updated successfully ✓");
      }
      setModal(null);
      load();
    } catch (e) {
      console.error("Save error:", e);
      toast(e.message || "Failed to save product", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await aDelete(`/products/${id}`);
      toast("Product deleted");
      // Clear selected if this product was selected
      setSelected((prev) => prev.filter((item) => item !== id));
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} products?`)) return;
    try {
      await aPost("/products/bulk-delete", { ids: selected });
      toast(`${selected.length} products deleted`);
      setSelected([]);
      load();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const getTotalStock = (sizes) => {
    if (!sizes) return 0;
    // Parse if it's a string
    let sizesArray = sizes;
    if (typeof sizes === "string") {
      try {
        sizesArray = JSON.parse(sizes);
      } catch (e) {
        return 0;
      }
    }
    if (!Array.isArray(sizesArray)) return 0;
    return sizesArray.reduce((sum, size) => sum + (Number(size.stock) || 0), 0);
  };

  const calculateTotalStock = () => {
    return form.sizes.reduce((sum, size) => sum + (Number(size.stock) || 0), 0);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSelected([]); // Clear selections when changing page
  };

  // Product Image Component with error handling
  const ProductImage = ({ imageUrl, productName }) => {
    const [imgError, setImgError] = useState(false);
    const normalizedUrl = normalizeImageUrl(imageUrl);

    if (!normalizedUrl || imgError) {
      return (
        <div
          style={{
            width: "50px",
            height: "50px",
            background: "#f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "4px",
          }}
        >
          <FiImage />
        </div>
      );
    }

    return (
      <img
        src={normalizedUrl}
        alt={productName}
        style={{
          width: "50px",
          height: "50px",
          objectFit: "cover",
          borderRadius: "4px",
        }}
        onError={() => setImgError(true)}
      />
    );
  };

  return (
    <div className="products-container" style={{ padding: "20px" }}>
      {/* Toolbar */}
      <div
        className="products-toolbar"
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <div
          className="toolbar-filters"
          style={{ display: "flex", gap: "10px" }}
        >
          <Input
            placeholder="Search products…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            style={{ minWidth: 240 }}
            icon={<FiSearch />}
          />
          <Select
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c._id} value={c.name}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
        <div
          className="toolbar-actions"
          style={{ display: "flex", gap: "10px" }}
        >
          {selected.length > 0 && (
            <Btn variant="red" onClick={handleBulkDelete}>
              <FiTrash2 /> Delete ({selected.length})
            </Btn>
          )}
          <Btn onClick={openAdd}>
            <FiPlus /> Add Product
          </Btn>
        </div>
      </div>

      <div
        className="products-meta"
        style={{ marginBottom: "15px", color: "#666" }}
      >
        <FiPackage /> {total} products found
      </div>

      <div className="products-table-card">
        <Table
          loading={loading}
          columns={[
            {
              key: "_id",
              label: "",
              render: (v, row) => (
                <input
                  type="checkbox"
                  checked={selected.includes(v)}
                  onChange={() =>
                    setSelected((prev) =>
                      prev.includes(v)
                        ? prev.filter((x) => x !== v)
                        : [...prev, v],
                    )
                  }
                />
              ),
            },
            {
              key: "image",
              label: "Image",
              render: (v, row) => (
                <ProductImage
                  imageUrl={v || row.image}
                  productName={row.name}
                />
              ),
            },
            { key: "name", label: "Name", render: (v) => <strong>{v}</strong> },
            { key: "category", label: "Category" },
            {
              key: "subcategory",
              label: "Subcategory",
              render: (v) =>
                v ? (
                  <span style={{ fontSize: "12px", color: "#666" }}>{v}</span>
                ) : (
                  <span style={{ color: "#ccc" }}>—</span>
                ),
            },
            { key: "brand", label: "Brand" },
            {
              key: "price",
              label: "Price",
              render: (v) => (
                <span style={{ color: "#e67e22", fontWeight: "bold" }}>
                  ₹{v?.toLocaleString()}
                </span>
              ),
            },
            {
              key: "stock",
              label: "Stock",
              render: (v, row) => {
                // Handle stock from sizes array
                let totalStock = v;
                if (row.sizes) {
                  let sizesArray = row.sizes;
                  if (typeof sizesArray === "string") {
                    try {
                      sizesArray = JSON.parse(sizesArray);
                    } catch (e) {
                      sizesArray = [];
                    }
                  }
                  if (Array.isArray(sizesArray)) {
                    totalStock = sizesArray.reduce(
                      (sum, size) => sum + (Number(size.stock) || 0),
                      0,
                    );
                  }
                }
                return (
                  <span
                    style={{
                      color: totalStock < 10 ? "#e74c3c" : "#27ae60",
                      fontWeight: "500",
                    }}
                  >
                    {totalStock}
                  </span>
                );
              },
            },
            {
              key: "sizes",
              label: "Sizes",
              render: (v) => {
                // Handle if v is a string (JSON from MySQL)
                let sizesArray = v;
                if (typeof v === "string") {
                  try {
                    sizesArray = JSON.parse(v);
                  } catch (e) {
                    sizesArray = [];
                  }
                }
                // Check if it's an array before calling map
                if (Array.isArray(sizesArray) && sizesArray.length > 0) {
                  return sizesArray.map((s) => s.name).join(", ");
                }
                return "—";
              },
            },
            {
              key: "inStock",
              label: "Status",
              render: (v) => <Badge status={v ? "In Stock" : "Out of Stock"} />,
            },
            {
              key: "featured",
              label: "Featured",
              render: (v) =>
                v ? <FiStar style={{ color: "#f1c40f" }} /> : "—",
            },
            {
              key: "_id",
              label: "Actions",
              render: (v, row) => (
                <div style={{ display: "flex", gap: "5px" }}>
                  <Btn size="sm" variant="ghost" onClick={() => openEdit(row)}>
                    <FiEdit2 /> Edit
                  </Btn>
                  <Btn size="sm" variant="red" onClick={() => handleDelete(v)}>
                    <FiTrash2 />
                  </Btn>
                </div>
              ),
            },
          ]}
          data={products}
          rowKey="id"
        />
      </div>
      <Pagination page={page} pages={pages} onChange={handlePageChange} />

      {/* Add/Edit Modal */}
      {modal && (
        <Modal
          title={modal === "add" ? "Add New Product" : "Edit Product"}
          onClose={() => setModal(null)}
        >
          <div
            style={{ padding: "20px", maxHeight: "70vh", overflowY: "auto" }}
          >
            {/* Basic Information */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "15px",
                marginBottom: "20px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                    fontSize: "13px",
                  }}
                >
                  Product Name *
                </label>
                <input
                  type="text"
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Enter product name"
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                    fontSize: "13px",
                  }}
                >
                  Brand
                </label>
                <input
                  type="text"
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                  value={form.brand}
                  onChange={(e) => setForm({ ...form, brand: e.target.value })}
                  placeholder="Enter brand name"
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                    fontSize: "13px",
                  }}
                >
                  <FiFolder style={{ marginRight: "4px" }} /> Category *
                </label>
                <select
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                  value={form.category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                >
                  <option value="">Select a category</option>
                  {categories.map((c) => (
                    <option key={c._id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                    fontSize: "13px",
                  }}
                >
                  <FiTag style={{ marginRight: "4px" }} /> Subcategory
                </label>
                <input
                  type="text"
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    background: "#f9f9f9",
                  }}
                  value={form.subcategory}
                  onChange={(e) =>
                    setForm({ ...form, subcategory: e.target.value })
                  }
                  placeholder="Auto-generated from category (or edit manually)"
                />
                <small
                  style={{
                    fontSize: "10px",
                    color: "#999",
                    display: "block",
                    marginTop: "4px",
                  }}
                >
                  Auto-generated from category, but you can edit manually
                </small>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                    fontSize: "13px",
                  }}
                >
                  Badge (NEW, HOT, SALE)
                </label>
                <input
                  type="text"
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                  value={form.badge || ""}
                  onChange={(e) => setForm({ ...form, badge: e.target.value })}
                  placeholder="e.g., NEW, HOT, SALE"
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                    fontSize: "13px",
                  }}
                >
                  Base Price (₹) *
                </label>
                <input
                  type="number"
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  placeholder="Enter base price"
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "bold",
                    fontSize: "13px",
                  }}
                >
                  Original Price (₹)
                </label>
                <input
                  type="number"
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                  }}
                  value={form.originalPrice || ""}
                  onChange={(e) =>
                    setForm({ ...form, originalPrice: e.target.value })
                  }
                  placeholder="Enter original price (for discount display)"
                />
              </div>
            </div>

            {/* Image Upload Section */}
            {/* Image Upload Section */}
            <div
              style={{
                marginBottom: "20px",
                border: "1px solid #eee",
                borderRadius: "8px",
                padding: "15px",
                background: "#fafafa",
              }}
            >
              <h4
                style={{
                  margin: "0 0 15px 0",
                  fontSize: "14px",
                  fontWeight: "bold",
                }}
              >
                Product Images
              </h4>

              <ImageUpload
                currentImage={form.image}
                onImageUploaded={(imageUrl) =>
                  setForm({ ...form, image: imageUrl })
                }
                onImageRemoved={() => setForm({ ...form, image: "" })}
                label="Main Product Image *"
              />

              <div style={{ marginTop: "20px" }}>
                <ImageUpload
                  currentImage={null}
                  onMultipleImagesUploaded={(images) => {
                    const imagesArray = Array.isArray(images)
                      ? images
                      : [images];
                    const currentImages = Array.isArray(form.images)
                      ? form.images
                      : [];
                    setForm({
                      ...form,
                      images: [...currentImages, ...imagesArray],
                    });
                  }}
                  label="Additional Images (Optional)"
                  multiple={true}
                  existingImages={Array.isArray(form.images) ? form.images : []}
                />

                {/* Display uploaded additional images - SAFE VERSION */}
                {(() => {
                  const imagesArray = Array.isArray(form.images)
                    ? form.images
                    : [];
                  if (imagesArray.length === 0) return null;

                  return (
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        marginTop: "10px",
                        flexWrap: "wrap",
                      }}
                    >
                      {imagesArray.map((img, idx) => (
                        <div key={idx} style={{ position: "relative" }}>
                          <img
                            src={normalizeImageUrl(img)}
                            alt={`Product ${idx + 1}`}
                            style={{
                              width: "80px",
                              height: "80px",
                              objectFit: "cover",
                              borderRadius: "4px",
                              border: "1px solid #ddd",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                          <button
                            onClick={async () => {
                              try {
                                await aDelete("/upload/image", {
                                  imageUrl: img,
                                });
                                const newImages = imagesArray.filter(
                                  (_, i) => i !== idx,
                                );
                                setForm({ ...form, images: newImages });
                              } catch (error) {
                                toast(error.message, "error");
                              }
                            }}
                            style={{
                              position: "absolute",
                              top: "-8px",
                              right: "-8px",
                              background: "#e74c3c",
                              color: "white",
                              border: "none",
                              borderRadius: "50%",
                              width: "22px",
                              height: "22px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "12px",
                            }}
                          >
                            <FiX />
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Sizes Management Section */}
            <div
              style={{
                marginBottom: "20px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                padding: "15px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "15px",
                }}
              >
                <h4
                  style={{
                    margin: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "14px",
                  }}
                >
                  <FaRuler /> Sizes & Stock Management
                </h4>
                <Btn size="sm" variant="ghost" onClick={addSize}>
                  <FiPlus /> Add Size Variant
                </Btn>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f5f5f5" }}>
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "left",
                          borderBottom: "1px solid #ddd",
                          fontSize: "13px",
                        }}
                      >
                        Size Name *
                      </th>
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "left",
                          borderBottom: "1px solid #ddd",
                          fontSize: "13px",
                        }}
                      >
                        Price (₹)
                      </th>
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "left",
                          borderBottom: "1px solid #ddd",
                          fontSize: "13px",
                        }}
                      >
                        Stock *
                      </th>
                      <th
                        style={{
                          padding: "10px",
                          textAlign: "center",
                          borderBottom: "1px solid #ddd",
                          fontSize: "13px",
                        }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.sizes.map((size, index) => (
                      <tr key={index}>
                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          <input
                            type="text"
                            style={{
                              width: "100%",
                              padding: "6px",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                            }}
                            placeholder="e.g., Regular, Large, XL"
                            value={size.name}
                            onChange={(e) =>
                              handleSizeChange(index, "name", e.target.value)
                            }
                            required
                          />
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          <input
                            type="number"
                            style={{
                              width: "100%",
                              padding: "6px",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                            }}
                            placeholder={`Leave empty for ₹${form.price || 0}`}
                            value={size.price || ""}
                            onChange={(e) =>
                              handleSizeChange(
                                index,
                                "price",
                                e.target.value ? Number(e.target.value) : null,
                              )
                            }
                          />
                          <small style={{ fontSize: "10px", color: "#999" }}>
                            Empty = use base price
                          </small>
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          <input
                            type="number"
                            style={{
                              width: "100%",
                              padding: "6px",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                            }}
                            placeholder="Stock quantity"
                            value={size.stock}
                            onChange={(e) =>
                              handleSizeChange(
                                index,
                                "stock",
                                Number(e.target.value),
                              )
                            }
                            min="0"
                            required
                          />
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            textAlign: "center",
                            borderBottom: "1px solid #eee",
                          }}
                        >
                          <Btn
                            size="sm"
                            variant="red"
                            onClick={() => removeSize(index)}
                            disabled={form.sizes.length === 1}
                            style={{ padding: "4px 8px" }}
                          >
                            <FiX /> Remove
                          </Btn>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: "#f9f9f9" }}>
                      <td
                        colSpan="2"
                        style={{
                          padding: "10px",
                          textAlign: "right",
                          fontWeight: "bold",
                          fontSize: "13px",
                        }}
                      >
                        Total Stock:
                      </td>
                      <td
                        colSpan="2"
                        style={{
                          padding: "10px",
                          fontWeight: "bold",
                          color: "#e67e22",
                        }}
                      >
                        <MdInventory /> {calculateTotalStock()} units
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Description */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                  fontSize: "13px",
                }}
              >
                <MdDescription /> Description
              </label>
              <textarea
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  minHeight: "100px",
                  fontSize: "13px",
                }}
                value={form.description || ""}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                placeholder="Product description..."
              />
            </div>

            {/* Featured Checkbox */}
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={form.featured || false}
                  onChange={(e) =>
                    setForm({ ...form, featured: e.target.checked })
                  }
                />
                <FiStar style={{ color: "#f1c40f" }} /> Featured Product
              </label>
            </div>

            {/* Modal Actions */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: "20px",
                paddingTop: "20px",
                borderTop: "1px solid #ddd",
              }}
            >
              <Btn variant="ghost" onClick={() => setModal(null)}>
                Cancel
              </Btn>
              <Btn onClick={handleSave} disabled={saving}>
                {saving
                  ? "Saving..."
                  : modal === "add"
                    ? "Create Product"
                    : "Update Product"}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
